import { StandardEvents } from '@shopify/events';

const SELECTOR = '.free-shipping-promise[data-threshold]';

/**
 * Keeps the product page's free shipping line true as the bag changes.
 *
 * Deliberately not a custom element. It was one, and the element was quietly
 * removed from the DOM by the page's own re-render on the first add -- the
 * paragraph inside it survived, the wrapper did not, and the listener went with
 * it. That failed silently and only on some products, which is the worst shape
 * a bug can take.
 *
 * So nothing here holds a reference to a node. The listener lives on document
 * for the life of the page, and the line is found again on every update. If the
 * page replaces the paragraph, the next update simply finds the new one.
 *
 * Every wording it can reach arrives as an attribute, so the sentences stay in
 * the locale file and this only picks between them. The single value it formats
 * is the amount still missing.
 */
function money(cents, format) {
  const whole = (cents / 100).toFixed(2);
  const withCommas = (value) => value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
    switch (token) {
      case 'amount_no_decimals':
      case 'amount_no_decimals_with_comma_separator':
        return withCommas(Math.round(cents / 100).toString());
      case 'amount_with_comma_separator':
        return withCommas(whole).replace('.', ',');
      default:
        return withCommas(whole);
    }
  });
}

/**
 * Whether one more of this variant can go in the bag. data-max is the stock
 * when Shopify caps it and blank when it doesn't, so a piece whose last one is
 * already in the cart stops being offered as the thing that clears the bar --
 * the add would only fail with "maximum quantity already in your cart".
 * @param {DOMStringMap} data
 * @param {Array<{variant_id: number, quantity: number}>} items
 */
function canAdd(data, items) {
  if (data.available !== 'true') return false;
  if (!data.max) return true;
  const inCart = items
    .filter((item) => String(item.variant_id) === data.variantId)
    .reduce((sum, item) => sum + item.quantity, 0);
  return inCart < Number(data.max);
}

/**
 * @param {DOMStringMap} data
 * @param {{total_price: number, total_discount?: number, items?: Array<{variant_id: number, quantity: number}>}} cart
 */
function resultFor(data, cart) {
  const total = cart.total_price;
  const missing = Number(data.threshold) - total;

  if (missing <= 0) return { state: 'earned', message: data.earned };
  // No "add this" with a discount in the bag: missing is measured on the
  // discounted total and data.price is the full price, so the promise could
  // overshoot. See blocks/free-shipping-promise.liquid.
  const discounted = (cart.total_discount ?? 0) > 0;
  if (!discounted && canAdd(data, cart.items ?? []) && Number(data.price) >= missing) {
    return { state: 'clears', message: data.clears };
  }
  if (total > 0) {
    return { state: 'remaining', message: data.remaining.replace('[amount]', money(missing, data.moneyFormat)) };
  }
  return { state: 'promise', message: data.promise };
}

async function write() {
  const line = document.querySelector(SELECTOR);
  if (!line) return;

  try {
    // no-store because a cached /cart.js answered with the previous total, and
    // the line then argued for money the shopper had just spent.
    const cart = await fetch('/cart.js', { cache: 'no-store' }).then((response) => response.json());
    const { state, message } = resultFor(line.dataset, cart);

    // The state drives the emphasis, so it has to move with the words. A quiet
    // line saying the offer is won, or a loud one saying it is not, would read
    // worse than a line that never updated at all.
    line.dataset.state = state;
    const text = line.querySelector('.free-shipping-promise__text');
    if (text) text.textContent = message;
  } catch {
    // Leave the rendered line alone. It is a true statement about the shop
    // either way, so a failed refresh should not blank it.
  }
}

// Twice. The event announces the change, but the cart it describes is not
// reliably readable at that instant: the first add after a fresh page load was
// consistently a step behind while every later one was correct. Writing again
// shortly after is what makes the first add behave like the rest.
document.addEventListener(StandardEvents.cartLinesUpdate, () => {
  write();
  setTimeout(write, 800);
});
